import { useState, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmModal,
  Input,
  ProviderLogo,
  Table,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useMcpServers, useDeleteMcpServer } from '@/hooks/useMcpServers';
import { useOrgMcpServers } from '@/hooks/useOrgMcpServers';
import { useMcpProviders } from '@/hooks/useMcpProviders';
import { useSpaceContext } from '@/contexts/SpaceContext';
import { useSpaceCredentials } from '@/hooks/useIdentityCredentials';
import type { ProviderSummary } from '@/types/identity';
import { meetsRole } from '@/lib/space-roles';
import { routes } from '@/lib/routes';
import { McpProviderPicker } from './McpProviderPicker';
import { CimdClientBadge } from './CimdClientBadge';
import type { McpServer } from '@/types';
import { deriveMcpStatus } from '@/types/mcp';
import type { McpProviderCatalogEntry, OrgMcpServer } from '@/types/mcp';

/**
 * Client-config cell. A manually-configured OAuth app comes from
 * optr-identity's provider summary — the app lives there (a papp row keyed
 * by serverId), never on the agent-core server config. Reading it off the
 * server row is what made every configured client render as "missing".
 * CIMD servers have no app anywhere by design — the server row's
 * `oauthClientMode` (set at provisioning) drives the badge.
 */
function clientCell(
  server: McpServer,
  providers: ProviderSummary[] | undefined,
): ReactNode {
  const app = providers?.find((p) => p.providerInstanceId === server.serverId);
  if (app?.oauthApp) return <span className="text-sm text-muted-foreground">Manual</span>;
  if (server.oauthClientMode === 'cimd') return <CimdClientBadge />;
  return <span className="text-sm text-muted-foreground">Not configured</span>;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function McpServerList() {
  const navigate = useNavigate();
  const { spaceId, role } = useSpaceContext();
  const { data: credentials } = useSpaceCredentials(spaceId);
  const isAdmin = meetsRole(role, 'admin');
  const { success, error: showError } = useToast();
  const { data, isLoading } = useMcpServers();
  const { data: orgServers, isLoading: orgServersLoading } = useOrgMcpServers();
  const { data: catalog } = useMcpProviders();
  const deleteServer = useDeleteMcpServer();

  const providersById = useMemo(() => {
    const m = new Map<string, McpProviderCatalogEntry>();
    for (const p of catalog?.providers ?? []) m.set(p.providerId, p);
    return m;
  }, [catalog]);

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    server: McpServer | null;
  }>({ isOpen: false, server: null });

  const servers = data ?? [];

  const filtered = useMemo(() => {
    if (!searchQuery) return servers;
    const query = searchQuery.toLowerCase();
    return servers.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.serverUrl.toLowerCase().includes(query),
    );
  }, [servers, searchQuery]);

  const handleDelete = async () => {
    if (!deleteModal.server) return;
    try {
      await deleteServer.mutateAsync(deleteModal.server.serverId);
      success('MCP server deleted');
      setDeleteModal({ isOpen: false, server: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete server');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (s: McpServer) => {
        const p = s.providerId ? providersById.get(s.providerId) : undefined;
        return (
          <div className="flex items-center gap-3">
            {s.providerId && (
              <ProviderLogo
                slug={s.providerId}
                displayName={p?.displayName}
                className="h-8 w-8"
                grayscale
              />
            )}
            <div className="min-w-0">
              <p className="font-medium text-foreground">{s.name}</p>
              <p className="text-muted-foreground text-xs mt-0.5 truncate max-w-xs">
                {s.providerId
                  ? (p?.displayName ?? 'Unknown provider')
                  : s.serverUrl}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'auth',
      header: 'Client',
      render: (s: McpServer) => clientCell(s, credentials?.providers),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s: McpServer) => {
        const status = deriveMcpStatus(s);
        return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
      },
    },
    {
      key: 'lastSync',
      header: 'Last Sync',
      render: (s: McpServer) => (
        <span className="text-sm text-muted-foreground">
          {formatRelativeTime(s.lastSyncedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (s: McpServer) =>
        isAdmin ? (
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setDeleteModal({ isOpen: true, server: s });
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
            >
              Delete
            </Button>
          </div>
        ) : null,
    },
  ];

  const orgColumns = [
    {
      key: 'name',
      header: 'Name',
      render: (s: OrgMcpServer) => {
        const p = s.providerId ? providersById.get(s.providerId) : undefined;
        return (
          <div className="flex items-center gap-3">
            {s.providerId && (
              <ProviderLogo
                slug={s.providerId}
                displayName={p?.displayName}
                className="h-8 w-8"
                grayscale
              />
            )}
            <div className="min-w-0">
              <p className="font-medium text-foreground">{s.name}</p>
              <p className="text-muted-foreground text-xs mt-0.5 truncate max-w-xs">
                {s.providerId
                  ? (p?.displayName ?? 'Unknown provider')
                  : s.serverUrl}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'source',
      header: 'Source',
      render: () => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="info" className="cursor-default">From org</Badge>
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            Managed by org admins
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s: OrgMcpServer) => {
        const status = deriveMcpStatus(s);
        return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
      },
    },
    {
      key: 'lastSync',
      header: 'Last Sync',
      render: (s: OrgMcpServer) => (
        <span className="text-sm text-muted-foreground">
          {formatRelativeTime(s.lastSyncedAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      {isAdmin && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            Available providers
          </h2>
          <McpProviderPicker
            providers={catalog?.providers ?? []}
            customServerAllowed={catalog?.customServerAllowed ?? true}
            onSelectProvider={(id) =>
              navigate(
                `${routes.console.spaceToolsMcpServerNew(spaceId)}?provider=${id}`,
              )
            }
            onSelectCustom={() =>
              navigate(
                `${routes.console.spaceToolsMcpServerNew(spaceId)}?provider=custom`,
              )
            }
          />
        </div>
      )}

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Configured servers
        </h2>
        <div className="flex gap-4 items-center">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Search MCP servers..."
              value={searchQuery}
              onChange={(e: { target: { value: string } }) =>
                setSearchQuery(e.target.value)
              }
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={filtered}
          keyExtractor={(s) => s.serverId}
          onRowClick={(s) =>
            navigate(routes.console.spaceToolsMcpServer(spaceId, s.serverId))
          }
          isLoading={isLoading}
          emptyMessage={
            searchQuery
              ? 'No MCP servers match your search.'
              : isAdmin
                ? 'No MCP servers configured. Pick a provider above to get started.'
                : 'No MCP servers have been configured for this space yet.'
          }
        />
      </div>

      {/* Org-installed MCP servers — available in every space. Surfacing them
          here keeps admins from accidentally double-installing a vendor that
          already exists at the org level. */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Organization MCP servers
        </h2>
        <Table
          columns={orgColumns}
          data={orgServers ?? []}
          keyExtractor={(s) => s.serverId}
          onRowClick={(s) =>
            navigate(routes.console.spaceToolsOrgMcpServer(spaceId, s.serverId))
          }
          isLoading={orgServersLoading}
          emptyMessage="No organization-managed MCP servers."
        />
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, server: null })}
        onConfirm={handleDelete}
        title="Delete MCP Server"
        message={`Are you sure you want to delete "${deleteModal.server?.name}"? This removes its tools from this space and revokes all per-user connections.`}
        confirmText="Delete"
        isLoading={deleteServer.isPending}
      />
    </div>
  );
}
